import type { ElectronContext } from '@mimir/common/context/applicationContext';
import type { KeldaConfigDto } from '@mimir/common/dto/keldaConfigDto';
import { readDirAsync } from '@mimir/common/util/fs';

interface KeldaWatchFolderForAme {
  path: string;
  keldaName: string;
}

/**
 * Given a list of kelda configs, this method tries to deduce the local paths corresponding to the AME watchfolders
 * configured on those Keldas. It does it for each Kelda+AME+preset by resolving this path -
 * `${conformSourceFolder}/../../AME/${ameName}/${presetName}` on the OS that Mimir Desktop is running on (Windows or Mac).
 */
export const getFlattenedAmeWatchFolderPaths = (applicationContext: ElectronContext, keldaConfigs: KeldaConfigDto[]): KeldaWatchFolderForAme[] => {
  const flattenedPaths: KeldaWatchFolderForAme[] = [];
  for (const config of keldaConfigs) {
    if (!config.conformSourceFolder || !applicationContext.nodeApi.path.isAbsolute(config.conformSourceFolder) || !config.ameWatchfolders || !config.amePresets) {
      continue;
    }

    const ameRoot = applicationContext.nodeApi.path.resolve(`${config.conformSourceFolder}`, '../../AME');
    for (const ameWatchfolder of config.ameWatchfolders) {
      for (const amePreset of config.amePresets) {
        flattenedPaths.push({
          keldaName: config.name,
          path: applicationContext.nodeApi.path.join(ameRoot, ameWatchfolder, amePreset)
        });
      }
    }
  }

  return flattenedPaths;
};

export const getFlattenedDvrWatchFolderPaths = (applicationContext: ElectronContext, keldaConfigs: KeldaConfigDto[]): KeldaWatchFolderForAme[] => {
  const flattenedPaths: KeldaWatchFolderForAme[] = [];
  for (const config of keldaConfigs) {
    if (!config.dvrConformSourceFolder || !applicationContext.nodeApi.path.isAbsolute(config.dvrConformSourceFolder) || !config.dvrWatchfolders || !config.dvrPresets) {
      continue;
    }

    const dvrRoot = applicationContext.nodeApi.path.resolve(`${config.dvrConformSourceFolder}`, '../../DVR');
    for (const dvrWatchfolder of config.dvrWatchfolders) {
      for (const dvrPreset of config.dvrPresets) {
        flattenedPaths.push({
          keldaName: config.name,
          path: applicationContext.nodeApi.path.join(dvrRoot, dvrWatchfolder, dvrPreset)
        });
      }
    }
  }

  return flattenedPaths;
};

// Just returns the absolute path of the first project file with the given extension found as a direct child of the given folder
export const getProjectFile = async (electronContext: ElectronContext, folderPath: string, projectFileExtension: string) => {
  const entries = await readDirAsync(electronContext.nodeApi, folderPath);
  const pathModule = electronContext.nodeApi.path;
  for (const name of entries) {
    // TODO: if folder name ends with the extension, we get a false positive.
    // But we have the challenge of statsResult.isFile getting stripped
    // away by Electron's IPC. We need to check in some other way.
    if (name.toLowerCase().endsWith(projectFileExtension)) {
      return pathModule.join(folderPath, name);
    }
  }
};
